import Splide from "@splidejs/splide/dist/js/splide.min";
import { EVENTS } from "../../../js/constants";

class ImageGallery {
  /**
   * @param {HTMLElement} element - image gallery element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        slide: ".js-ImageGallery-slide",
        content: ".js-ImageGallery-content",
        slideNumber: ".js-ImageGallery-slideNumber",
        slideTitle: ".js-ImageGallery-title",
        info: ".js-ImageGallery-info",
        clone: ".splide__slide--clone",
      },
      classes: {
        visible: "is-visible",
        move: "is-moving",
      },
      classMap: [
        {
          selector: ".js-ImageGallery-track",
          cssClass: "splide__track",
        },
        {
          selector: ".js-ImageGallery-list",
          cssClass: "splide__list",
        },
        {
          selector: ".js-ImageGallery-slide",
          cssClass: "splide__slide",
        },
        {
          selector: ".js-ImageGallery-arrows",
          cssClass: "splide__arrows",
        },
        {
          selector: ".js-ImageGallery-arrow",
          cssClass: "splide__arrow",
        },
        {
          selector: ".js-ImageGallery-arrow--next",
          cssClass: "splide__arrow--next",
        },
        {
          selector: ".js-ImageGallery-arrow--prev",
          cssClass: "splide__arrow--prev",
        },
      ],
    };

    this._onMounted = this._onMounted.bind(this);
    this._onMoveStart = this._onMove.bind(this, true);
    this._onMoveEnd = this._onMove.bind(this, false);
    this._onActive = this._onActive.bind(this);

    this.initialized = false;

    this.prevSlideIndex = 0;
    this.lastSlideIndex =
      [...this.element.querySelectorAll(this.config.selectors.slide)].length -
      1;

    this.content = this.element.querySelector(this.config.selectors.content);

    this._init();
  }

  /**
   * init
   */
  _init() {
    this._addClasses();
    this._initSplide();
    this._bindSplideEvents();
    this._mountSplide();
  }

  /**
   * prepares markup and adds css classes for splide
   * splide needs specific css classes to work with, that can't be configured
   */
  _addClasses() {
    this.config.classMap.forEach(({ selector, cssClass }) => {
      const elements = [...this.element.querySelectorAll(selector)];
      elements.forEach((el) => el.classList.add(cssClass));
    });

    this.content.classList.add("splide");
  }

  /**
   * inits splide with dedicated config
   */
  _initSplide() {
    this.splideConfig = {
      autoWidth: true,
      clones: 3,
      flickPower: 80,
      focus: "center",
      gap: "var(--ImageGallery-slide-gap)",
      keyboard: "focused",
      pagination: false,
      slideFocus: false,
      swipeDistanceThreshold: 1000,
      type: "loop",
      i18n: {
        prev: "vorheriges Bild",
        next: "nächstes Bild",
        first: "zum ersten Bild",
        last: "zum letzten Bild",
      },
    };

    this.splide = new Splide(this.content, this.splideConfig);
  }

  /**
   * mounts splide to dom
   */
  _mountSplide() {
    this.splide.mount();
  }

  /**
   * binds splide events
   *
   * @private
   */
  _bindSplideEvents() {
    this.splide.on("mounted", this._onMounted);
    this.splide.on("drag", this._onMoveStart);
    this.splide.on("move", this._onMoveStart);
    this.splide.on("moved", this._onMoveEnd);
    this.splide.on("active", this._onActive);
  }

  /**
   * handles splide movement
   * can be triggered by drag or by click on arrow button
   *
   * @param {boolean} state - start or end of movement
   * @private
   */
  _onMove(state) {
    this.content.classList.toggle(this.config.classes.move, state);
  }

  /**
   * handles mount of splide and hides clones for screenreaders.
   * readers should have the native experience of a list with all initial items
   *
   * @private
   */
  _onMounted() {
    const clones = [
      ...this.element.querySelectorAll(this.config.selectors.clone),
    ];
    clones.forEach((clone) => clone.setAttribute("aria-hidden", true));

    this.initialized = true;
  }

  /**
   * handles any changes of the active slide
   * sends a custom event with details to the active slide and slider direction (left/right)
   * splide also interprets the first render and changes to cloned slides as active slides changes - these changes should be ignored
   *
   * @param {object} nextSlide - slide which becomes active after slider has moved
   */
  _onActive(nextSlide) {
    if (!this.initialized || nextSlide.isClone) {
      return;
    }

    const nextSlideIndex = nextSlide.index;

    const loopsToFirstSlideFromEnd =
      nextSlideIndex === 0 && this.prevSlideIndex === this.lastSlideIndex;

    const loopsToLastSlideFromStart =
      this.prevSlideIndex === 0 && nextSlideIndex === this.lastSlideIndex;

    const isNextSlide = nextSlideIndex > this.prevSlideIndex;

    const slideDirection =
      loopsToLastSlideFromStart || !(loopsToFirstSlideFromEnd || isNextSlide)
        ? "previous item"
        : "next item";

    const slideTitleElement = nextSlide.slide.querySelector(
      this.config.selectors.slideTitle
    );

    const slideTitle = slideTitleElement ? slideTitleElement.textContent : "";

    const slideNumberElement = nextSlide.slide.querySelector(
      this.config.selectors.slideNumber
    );

    const slideNumber = slideNumberElement
      ? slideNumberElement.textContent
      : "";

    this.element.dispatchEvent(
      new CustomEvent(EVENTS.GALLERY_INTERACTION, {
        detail: {
          direction: slideDirection,
          title: slideTitle,
          number: slideNumber,
        },
      })
    );

    this.prevSlideIndex = nextSlideIndex;
  }
}

export default ImageGallery;
